// Pure transform for the coverage-first Workload board. No React, no IO — turns
// the raw WorkloadBoard payload + UI filters into a render model. This is where
// the grouping (Hotel > Reparto > Persone), coverage counting, away-day
// detection and CCNL badge thresholds live, so they are unit-testable.

import type { WorkloadBoard, CoverageLevel, BoardShift, Leave } from "@praesto/shared-types";
import { departmentName } from "@praesto/ui-kit";

export type BoardCell =
  | { kind: "shift"; slot: "M" | "P" | "N" | "X"; status: "draft" | "published" | "covered" }
  | { kind: "away"; departmentName: string }
  // Bound to the shared LeaveType rather than re-spelling it: a new leave type
  // must show up on the rota board, not silently fall through to "empty" and make
  // an absent person look available.
  | { kind: "leave"; leaveType: Leave["type"] }
  | { kind: "empty" };

export interface BoardRow {
  userId: string;
  displayName: string;
  photoUrl?: string;
  badges: { overHours?: number; consecutiveNights?: number };
  hoursInGroup: number;
  deltaSign: "over" | "warn" | "low" | "ok";
  cells: BoardCell[];
}

export interface CoverageCell {
  date: string;
  assigned: number;
  required: number | null;
  level: CoverageLevel;
}

export interface BoardDept {
  id: string;
  name: string;
  coverage: CoverageCell[];
  rows: BoardRow[];
}

export interface BoardHotel {
  id: string;
  name: string;
  gaps: number;
  departments: BoardDept[];
}

export interface BoardModel {
  hotels: BoardHotel[];
  shownPersons: number;
  totalPersons: number;
  truncated: boolean;
}

export interface BoardFilters {
  search: string;
  departmentId: string;
}

// Slot → worked hours. Mirrors slotTiming() in @praesto/shifts (M 7h, P 8h, N 9h).
const SLOT_HOURS: Record<"M" | "P" | "N" | "X", number> = { M: 7, P: 8, N: 9, X: 0 };

function addDaysISO(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

export function buildBoardModel(
  board: WorkloadBoard,
  weekStart: string,
  days: number,
  filters: BoardFilters,
): BoardModel {
  const dayDates = Array.from({ length: days }, (_, i) => addDaysISO(weekStart, i));
  const deptName = new Map(board.departments.map((d) => [d.id, departmentName(d)]));
  const search = filters.search.trim().toLowerCase();

  // Index every user's shifts by date for O(1) cell lookups.
  const shiftsByUserDate = new Map<string, BoardShift[]>();
  for (const s of board.shifts) {
    const k = `${s.assignedUserId}|${s.date}`;
    const arr = shiftsByUserDate.get(k);
    if (arr) arr.push(s);
    else shiftsByUserDate.set(k, [s]);
  }
  const leavesByUser = new Map<string, Leave[]>();
  for (const l of board.leaves) {
    const arr = leavesByUser.get(l.userId);
    if (arr) arr.push(l);
    else leavesByUser.set(l.userId, [l]);
  }

  const allPersons = new Set<string>();
  const shownPersons = new Set<string>();

  const hotels: BoardHotel[] = board.hotels.map((h) => {
    let gaps = 0;
    const departments: BoardDept[] = board.departments
      .filter((d) => d.hotelId === h.id)
      .filter((d) => !filters.departmentId || d.id === filters.departmentId)
      .map((d) => {
        const deptShifts = board.shifts.filter(
          (s) => s.departmentId === d.id && s.hotelId === h.id,
        );
        // Rows come from BOTH who is scheduled here and who belongs here.
        // Shifts alone answer "who is loaded?" but never "who is free?", and
        // an unscheduled week would render as an empty table.
        //
        // Membership stores the department CODE (what DepartmentMultiSelect
        // writes), while departments are keyed by `id` — matching on `id`
        // would silently place nobody.
        // `?? []` because this payload crosses the wire: a browser running a
        // newer bundle than the deployed API (or mid-deploy) would otherwise
        // take the whole board down on a field the type says is always there.
        const memberIds = (board.members ?? [])
          .filter((m) => m.hotelId === h.id && m.departments.includes(d.code))
          .map((m) => m.userId);
        const personIds = [...new Set([...deptShifts.map((s) => s.assignedUserId), ...memberIds])];
        personIds.forEach((p) => allPersons.add(p));

        const coverage: CoverageCell[] = dayDates.map((date) => {
          const assigned = new Set(
            deptShifts
              .filter((s) => s.date === date && s.slot !== "X")
              .map((s) => s.assignedUserId),
          ).size;
          const required = board.requiredPerDept[d.id] ?? null;
          let level: CoverageLevel = "unknown";
          if (required !== null) {
            level = assigned < required ? "bad" : assigned === required ? "warn" : "ok";
          }
          if (level === "bad") gaps += 1;
          return { date, assigned, required, level };
        });

        const rows: BoardRow[] = personIds
          .filter(
            (uid) =>
              !search || (board.users[uid]?.displayName ?? "").toLowerCase().includes(search),
          )
          .map((uid) => {
            shownPersons.add(uid);
            const wl = board.workload[uid];
            const cells: BoardCell[] = dayDates.map((date) => {
              const dayShifts = shiftsByUserDate.get(`${uid}|${date}`) ?? [];
              const here = dayShifts.find((s) => s.departmentId === d.id && s.hotelId === h.id);
              if (here) return { kind: "shift", slot: here.slot, status: here.status };
              const elsewhere = dayShifts[0];
              if (elsewhere)
                return {
                  kind: "away",
                  departmentName: deptName.get(elsewhere.departmentId) ?? "—",
                };
              const lv = (leavesByUser.get(uid) ?? []).find(
                (l) => date >= l.startDate && date <= l.endDate,
              );
              if (lv) return { kind: "leave", leaveType: lv.type };
              return { kind: "empty" };
            });
            const hoursInGroup = deptShifts
              .filter((s) => s.assignedUserId === uid)
              .reduce((acc, s) => acc + SLOT_HOURS[s.slot], 0);
            const badges: BoardRow["badges"] = {};
            if (wl?.overAllocation) badges.overHours = Math.round(wl.hoursWorked);
            if (wl && wl.consecutiveNights >= board.maxConsecutiveNights)
              badges.consecutiveNights = wl.consecutiveNights;
            const deltaSign: BoardRow["deltaSign"] = !wl
              ? "ok"
              : wl.deltaHours > 0
                ? "over"
                : wl.deltaHours < -4
                  ? "low"
                  : "ok";
            return {
              userId: uid,
              displayName: board.users[uid]?.displayName ?? uid,
              ...(board.users[uid]?.photoUrl ? { photoUrl: board.users[uid]!.photoUrl } : {}),
              badges,
              hoursInGroup,
              deltaSign,
              cells,
            };
          });

        return { id: d.id, name: departmentName(d), coverage, rows };
      });

    return { id: h.id, name: h.name, gaps, departments };
  });

  return {
    hotels,
    shownPersons: shownPersons.size,
    totalPersons: allPersons.size,
    truncated: board.truncated,
  };
}
